/**
 * Agent-based User Response Flow
 *
 * Replaces the old flow (classifyCategory → generateTextResponse → matchComponents)
 * with the multi-agent architecture:
 *
 * 1. Semantic search (cached match) — reuses existing ConversationSearch
 * 2. MainAgent.handleQuestion() — routes, queries sources, analyzes (ONE LLM call)
 * 3. If data was queried → matchComponentsFromAnalysis() on the BaseLLM instance
 *
 * No separate classification LLM call — the MainAgent handles routing internally
 * via source summaries (~100 tokens each) instead of full schemas.
 */

import { Component, T_RESPONSE, LLMProvider } from '../types';
import { convertQuestionsToActions } from './utils';
import { logger } from '../utils/logger';
import { userPromptErrorLogger } from '../utils/user-prompt-error-logger';
import { queryCache } from '../utils/query-cache';
import type { Action } from '../threads/action';
import ConversationSearch from './conversation-search';
import { StreamBuffer } from './stream-buffer';
import type { ExternalTool } from './services/tool-executor-service';
import KB from './knowledge-base';

// Multi-agent system
import { MainAgent, DEFAULT_AGENT_CONFIG, generateAgentComponents } from './agents';
import type { AgentConfig, WorkflowDescriptor } from './agents';

// Script flow
import { ScriptStore, ScriptMatcher, runScript, generateScriptComponents, assembleComponentsFromSpecs } from './scripts';
import type { ScriptComponentSpec } from './scripts';
import type { ExecutedToolInfo } from './services/tool-executor-service';

// LLM provider singletons (for next questions generation)
import { anthropicLLM } from './anthropic';
import { groqLLM } from './groq';
import { geminiLLM } from './gemini';
import { openaiLLM } from './openai';
import type { BaseLLM } from './base-llm';

import {
	KNOWLEDGE_BASE_TOP_K,
	TOOL_TRACKING_SAMPLE_ROWS,
	MAX_ROWS_RENDERED,
} from './constants';

import { LLM, RequestAbortedError } from '../llm';
import { promptLoader } from './prompt-loader';
import { getCurrentDateTimeForPrompt } from '../utils/datetime';

/**
 * Walk a cached container/component tree and return true if any leaf component
 * targets the `script_dataset` sentinel whose queryId is no longer in the
 * query cache. Such components are unrecoverable from the conversation cache
 * alone — the post-JS computed data they reference lives only in queryCache,
 * not in `_queryMap`, so `rehydrateCachedComponent` can't reconstruct it.
 *
 * Used to fall through to the agent flow (which can re-run the saved script
 * via the script matcher) instead of returning a UIBlock whose components
 * will render the "Script dataset expired" sentinel error.
 */
function hasExpiredScriptDataset(component: any): boolean {
	if (!component || typeof component !== 'object') return false;

	const et = component?.props?.externalTool;
	if (et?.toolId === 'script_dataset') {
		const qid = et?.parameters?.queryId;
		if (typeof qid === 'string' && qid.length > 0) {
			// queryId is an encrypted token containing the SQL itself — `getQuery`
			// only returns null on decryption failure. The data cache is a separate
			// in-memory map looked up via the token; that's what evicts. So we
			// need to check `.data`, not the whole entry. Empty data means the
			// frontend will hit the script_dataset sentinel's "expired" error.
			const stored = queryCache.getQuery(qid);
			if (!stored || stored.data == null) return true;
		}
	}

	const children = component?.props?.config?.components;
	if (Array.isArray(children)) {
		for (const child of children) {
			if (hasExpiredScriptDataset(child)) return true;
		}
	}

	return false;
}

/**
 * Recover the recipe + params bound to a cached component's expired
 * script_dataset, so a TTL-expired exact match can re-run THAT exact recipe
 * directly — no ScriptMatcher LLM call. The script_dataset queryId is a
 * self-contained encrypted token holding `{ kind, recipeId, params, datasetRef }`
 * (see storeScriptDatasetQuery), so `getQuery` decrypts the descriptor even
 * after the data cache evicted. Returns undefined for real-SQL datasets (no
 * recipeId) or undecryptable tokens — caller falls back to the LLM matcher.
 */
function extractBoundRecipe(component: any): { recipeId: string; params: Record<string, any> } | undefined {
	if (!component || typeof component !== 'object') return undefined;

	const et = component?.props?.externalTool;
	if (et?.toolId === 'script_dataset') {
		const qid = et?.parameters?.queryId;
		if (typeof qid === 'string' && qid.length > 0) {
			const stored = queryCache.getQuery(qid);
			const desc = stored?.query;
			if (desc && typeof desc === 'object' && desc.kind === 'script_dataset' && typeof desc.recipeId === 'string') {
				return { recipeId: desc.recipeId, params: desc.params || {} };
			}
		}
	}

	const children = component?.props?.config?.components;
	if (Array.isArray(children)) {
		for (const child of children) {
			const found = extractBoundRecipe(child);
			if (found) return found;
		}
	}

	return undefined;
}

/**
 * Re-hydrate a cached component by re-registering its SQL queries in the query cache.
 * The cached component contains a `_queryMap` (queryId → SQL) persisted alongside it.
 * We deep-clone to avoid mutating the original cached object (so future cache hits still work).
 */
function rehydrateCachedComponent(component: any): any {
	const qMap = component?.props?.config?._queryMap;
	if (!qMap || typeof qMap !== 'object' || Object.keys(qMap).length === 0) return component;

	// Deep clone — don't mutate the cached original
	const cloned = JSON.parse(JSON.stringify(component));

	// Check if all existing queryIds are still valid — if so, reuse them as-is
	const allValid = Object.keys(qMap).every(qId => queryCache.getQuery(qId) !== null);
	if (allValid) {
		logger.info(`[AgentFlow] All ${Object.keys(qMap).length} cached queryIds still valid — reusing`);
		delete cloned.props.config._queryMap;
		return cloned;
	}

	// Some/all queryIds expired — re-register and replace with fresh queryIds
	const idMap: Record<string, string> = {};
	for (const [oldId, sql] of Object.entries(qMap)) {
		if (queryCache.getQuery(oldId) !== null) {
			idMap[oldId] = oldId; // Still valid, keep it
		} else {
			idMap[oldId] = queryCache.storeQuery(sql); // Expired, re-register
		}
	}

	// Replace queryIds in child components
	const components = cloned.props?.config?.components;
	if (Array.isArray(components)) {
		for (const comp of components) {
			// externalTool.parameters.queryId
			const etqId = comp.props?.externalTool?.parameters?.queryId;
			if (etqId && idMap[etqId]) {
				comp.props.externalTool.parameters.queryId = idMap[etqId];
			}
			// direct props.queryId
			if (comp.props?.queryId && idMap[comp.props.queryId]) {
				comp.props.queryId = idMap[comp.props.queryId];
			}
		}
	}

	// Remove _queryMap from the CLONE only (original stays intact for future cache hits)
	delete cloned.props.config._queryMap;

	const rehydratedCount = Object.values(idMap).filter(newId => !Object.keys(qMap).includes(newId)).length;
	logger.info(`[AgentFlow] Re-hydrated ${rehydratedCount}/${Object.keys(qMap).length} expired queries with fresh queryIds`);
	return cloned;
}

/**
 * Get the BaseLLM instance for a given provider name.
 */
function getLLMInstance(provider: LLMProvider): BaseLLM {
	switch (provider) {
		case 'anthropic': return anthropicLLM;
		case 'groq': return groqLLM;
		case 'gemini': return geminiLLM;
		case 'openai': return openaiLLM;
		default: return anthropicLLM;
	}
}

/**
 * Build the queryId for a script-produced dataset.
 *
 * For VIRTUAL datasets (ctx.emit / the synthetic `computed:_final` entry — post-JS
 * data with no re-runnable SQL), the encrypted token holds a regeneration
 * descriptor `{ kind, recipeId, params, datasetRef }` instead of a placeholder
 * SQL string. On cache expiry the backend re-runs the saved recipe to produce
 * FRESH data (no stale persistence) — see backend/docs/SCRIPT-FLOW-SCALING-ISSUES.md #6
 * and project memory `project_script_dataset_expiry_fix`.
 *
 * Real SQL datasets keep storing their SQL — they already self-heal by re-executing.
 */
/**
 * Safety net for the "summary row prepended to detail rows" anti-pattern.
 *
 * Components derive their columns/axes from the dataset's rows, so a dataset that
 * mixes two row shapes (e.g. a single {Verdict, Total_Decidable} roll-up followed
 * by many {District, Gap, ...} detail rows) renders the odd rows as N/A — and a
 * bar chart over it shows one real bar plus N empty "N/A" bars. The authoring
 * prompt forbids this, but Haiku still slips occasionally, so we defensively drop
 * the minority shape here, leaving one homogeneous dataset. No-op when the rows
 * are already uniform (the overwhelmingly common case).
 *
 * Keeps the LARGEST shape group (detail rows are always the majority vs a lone
 * summary). On a genuine tie we leave the data untouched — better N/A than
 * silently dropping half a legitimately-mixed dataset.
 */
function homogenizeDataset(rows: any[]): any[] {
	if (!Array.isArray(rows) || rows.length < 2) return rows;
	const sig = (r: any) =>
		r && typeof r === 'object' && !Array.isArray(r)
			? Object.keys(r).sort().join('|')
			: `__scalar__:${typeof r}`;
	const groups = new Map<string, any[]>();
	for (const r of rows) {
		const k = sig(r);
		(groups.get(k) ?? groups.set(k, []).get(k)!).push(r);
	}
	if (groups.size <= 1) return rows; // already homogeneous

	let best: any[] = [];
	let tie = false;
	for (const g of groups.values()) {
		if (g.length > best.length) { best = g; tie = false; }
		else if (g.length === best.length) { tie = true; }
	}
	if (tie || best.length === rows.length) return rows; // ambiguous — leave as-is

	logger.warn(
		`[AgentFlow] Heterogeneous script dataset: dropped ${rows.length - best.length} odd-shaped ` +
		`row(s) of ${rows.length} (kept the ${best.length}-row majority shape) to avoid N/A cells. ` +
		`The script should emit summary/detail as SEPARATE datasets.`,
	);
	return best;
}

function storeScriptDatasetQuery(
	q: { sourceId: string; sql: string; data: any[]; count: number; virtual?: boolean },
	ctx: { recipeId?: string; params?: Record<string, any> },
): string {
	// Drop a stray summary/verdict row mixed into detail rows before caching, so the
	// cached payload the UI renders (and the component generator that reuses this same
	// `q`) sees one uniform shape. Mutates `q` so both stay consistent.
	const homogenized = homogenizeDataset(q.data);
	if (homogenized.length !== q.data.length) {
		q.data = homogenized;
		q.count = homogenized.length;
	}

	// STAGE 3 (RENDER) cap: the cached payload is what the UI component draws and
	// what the data cache holds, so bound it here regardless of how many rows the
	// script's SQL returned. A script that needs to show more should pre-aggregate;
	// the cap is a safety net (and keeps the in-memory cache from holding huge rows).
	const data = q.data.length > MAX_ROWS_RENDERED ? q.data.slice(0, MAX_ROWS_RENDERED) : q.data;
	if (q.data.length > MAX_ROWS_RENDERED) {
		logger.warn(`[AgentFlow] Script dataset "${q.sourceId}" had ${q.data.length} rows — capped to ${MAX_ROWS_RENDERED} for render/cache. Consider pre-aggregating in the script.`);
	}
	const count = data.length;
	if (q.virtual && ctx.recipeId) {
		const descriptor = {
			kind: 'script_dataset' as const,
			recipeId: ctx.recipeId,
			params: ctx.params || {},
			datasetRef: q.sourceId,
		};
		return queryCache.storeQuery(JSON.stringify(descriptor), { data, count });
	}
	return queryCache.storeQuery(q.sql, { data, count });
}

/**
 * Follow-up actions policy (two paths):
 *  - FRESH ask: the component generator emits 3 actions in its OWN response
 *    (script-components prompt) so components + actions render together, in one
 *    LLM call — no separate generateNextQuestions round-trip.
 *  - REPLAY (TTL-expired re-run): we do NOT generate/cache actions here. The
 *    response returns with empty actions and the frontend fetches them on demand
 *    via getActions (ACTIONS_REQ) — so they always reflect the CURRENT data (the
 *    source may have changed since the recipe was authored) and the user sees the
 *    dashboard first, with actions arriving a couple of seconds later.
 */

/**
 * Main entry point for the agent-based user response flow.
 * Drop-in replacement for `get_user_response()` with the same signature and return type.
 */
export const get_agent_user_response = async (
	prompt: string,
	components: Component[],
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	conversationHistory?: string,
	streamCallback?: (chunk: string) => void,
	collections?: any,
	externalTools?: any[],
	userId?: string,
	mainAgentModel?: string,
	sourceAgentModel?: string,
	workflows?: WorkflowDescriptor[],
	signal?: AbortSignal,
): Promise<T_RESPONSE> => {
	const startTime = Date.now();
	const providers = llmProviders || ['anthropic'];
	const provider = providers[0]; // Use first provider
	const llmInstance = getLLMInstance(provider);

	// Clear prompt log file for new request and log the user prompt
	logger.clearFile();
	logger.logLLMPrompt('agentUserResponse', 'user', `User Prompt: ${prompt}`);

	logger.info(`[AgentFlow] Starting | provider: ${provider} | prompt: "${prompt.substring(0, 50)}..."`);

	try {
		// ============================================
		// STEP 1: Exact prompt match (cached replay)
		// ============================================
		// Indexed DB lookup on the stored user_prompt — no embeddings, no rerank.
		// Only replays when the prompt is byte-for-byte identical to a previous one.
		// Similar-but-not-identical questions deliberately fall through to the agent.

		// Set when an exact match's cached dashboard expired but we recovered the
		// recipe it was bound to — lets the matcher section re-run that exact recipe
		// directly, skipping the ScriptMatcher LLM call.
		let directReplay: { recipeId: string; params: Record<string, any> } | undefined;

		const conversationMatch = await ConversationSearch.findExactMatch({
			userPrompt: prompt,
			collections,
			userId,
		});

		if (conversationMatch) {
			const rawComponent = conversationMatch.uiBlock?.component || conversationMatch.uiBlock?.generatedComponentMetadata;
			const isValidComponent = rawComponent && typeof rawComponent === 'object' && Object.keys(rawComponent).length > 0;
			const component = isValidComponent ? rawComponent : null;
			const cachedTextResponse = conversationMatch.uiBlock?.analysis || conversationMatch.uiBlock?.textResponse || conversationMatch.uiBlock?.text || '';

			// Script-flow safety: post-JS computed data lives only in the in-memory
			// queryCache, and rehydrateCachedComponent only knows how to re-register
			// SQL queries (from `_queryMap`) — it can't reconstruct script_dataset
			// payloads. If any cached component references a script_dataset queryId
			// that's no longer cached, the UIBlock would render the "Script dataset
			// expired" sentinel error. Fall through to the agent flow instead — the
			// script matcher will replay the saved recipe and produce fresh data.
			if (component && hasExpiredScriptDataset(component)) {
				// Recover the recipe bound to this cached dashboard so we can re-run
				// it directly (fresh data) WITHOUT paying the ScriptMatcher LLM call —
				// we already know exactly which script produced this conversation.
				// Falls back to the full matcher if the binding can't be recovered.
				directReplay = extractBoundRecipe(component);
				logger.info(
					directReplay
						? `[AgentFlow] Exact match w/ expired script_dataset — will re-run bound recipe ${directReplay.recipeId} directly (skip matcher LLM)`
						: `[AgentFlow] Exact match found but cached UIBlock has expired script_dataset — falling through to script matcher`,
				);
			} else {
				if (streamCallback && cachedTextResponse) {
					streamCallback(cachedTextResponse);
				}

				const elapsedTime = Date.now() - startTime;
				logger.info(`[AgentFlow] Exact match — returning cached result (${elapsedTime}ms)`);

				// Re-hydrate cached component: re-register expired queryIds in the query cache
				const rehydratedComponent = component ? rehydrateCachedComponent(component) : null;

				return {
					success: true,
					data: {
						text: cachedTextResponse,
						component: rehydratedComponent,
						actions: conversationMatch.uiBlock?.actions || [],
						reasoning: `Exact match from previous conversation`,
						method: `${provider}-agent-exact-match`,
						semanticSimilarity: 1,
					},
					errors: []
				};
			}
		}

		logger.info(`[AgentFlow] No exact match — proceeding with agent`);

		// ============================================
		// STEP 2: MainAgent — route, query, analyze
		// ============================================

		// Resolve API key for the provider
		const apiKey = (() => {
			switch (provider) {
				case 'anthropic': return anthropicApiKey;
				case 'groq': return groqApiKey;
				case 'gemini': return geminiApiKey;
				case 'openai': return openaiApiKey;
				default: return anthropicApiKey;
			}
		})();

		// Convert externalTools to ExternalTool format
		const agentTools: ExternalTool[] = (externalTools || []).map(t => ({
			id: t.id,
			name: t.name,
			description: t.description,
			toolType: t.toolType,
			fullSchema: t.fullSchema,
			schemaTier: t.schemaTier,
			schemaSearchFn: t.schemaSearchFn,
			fn: t.fn,
			limit: t.limit,
			outputSchema: t.outputSchema,
			executionType: t.executionType,
			userProvidedData: t.userProvidedData,
			params: t.params,
		})) as any[];

		// Fetch knowledge base context (global + user + query-matched)
		let globalKnowledgeBase = '';
		let knowledgeBaseContext = '';
		if (collections) {
			const kbResult = await KB.getAllKnowledgeBase({
				prompt,
				collections,
				userId,
				topK: KNOWLEDGE_BASE_TOP_K,
			});
			globalKnowledgeBase = kbResult.globalContext || '';
			// Combine user-specific + query-matched as dynamic context
			const dynamicParts: string[] = [];
			if (kbResult.userContext) {
				dynamicParts.push('## User-Specific Knowledge Base\n' + kbResult.userContext);
			}
			if (kbResult.queryContext) {
				dynamicParts.push('## Relevant Knowledge Base (Query-Matched)\n' + kbResult.queryContext);
			}
			knowledgeBaseContext = dynamicParts.join('\n\n') || '';
		}

		// Derive projectId from tool IDs — every source tool carries a project-scoped ID.
		// Used to scope embedding searches to this project.
		const projectId = (() => {
			const firstSrcTool = agentTools.find(t => (t as any).toolType !== 'direct');
			const m = firstSrcTool?.id?.match(/^(\w+)-([a-f0-9]+)_/);
			return m ? m[2] : undefined;
		})();

		// Build agent config
		const agentConfig: AgentConfig = {
			...DEFAULT_AGENT_CONFIG,
			apiKey: apiKey || undefined,
			mainAgentModel: mainAgentModel || DEFAULT_AGENT_CONFIG.mainAgentModel,
			sourceAgentModel: sourceAgentModel || DEFAULT_AGENT_CONFIG.sourceAgentModel,
			globalKnowledgeBase,
			knowledgeBaseContext,
			collections,
			projectId,
		};

		// Create stream buffer
		const streamBuffer = new StreamBuffer(streamCallback);

		// ============================================
		// STEP 1.5: Script Matching (reuse existing script)
		// ============================================

		const scriptStore = new ScriptStore({ collections, projectId });

		// Per-turn unique suffix used for draft filenames. Keeps concurrent turns
		// (multiple users on the same project) from clobbering each other's draft
		// files in scripts-store/. Drops off the filename when the draft is
		// promoted to verified — see ScriptStore.promoteToVerified().
		const turnId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

		// Fork-and-adapt context — set when the matcher returns tier='near' AND
		// the parent's fork-depth is below the cap. Carried through MainAgent and
		// applied at save time so the resulting recipe records its parentage.
		// See backend/docs/SCRIPT-FLOW-FORK-ADAPT.md.
		const FORK_DEPTH_CAP = 3;
		let forkContext: {
			parentRecipeId: string;
			parentName: string;
			parentDepth: number;
			parentBody: string;
			gaps: string[];
			modificationHint?: string;
		} | undefined;

		if ((await scriptStore.count()) > 0) {
			// TTL-expired exact-match fast path: when we recovered the recipe that
			// produced this very conversation (directReplay), re-run it directly as a
			// high-tier match — skip the ScriptMatcher LLM entirely. The recipe + its
			// original params are authoritative; no re-matching needed. If the recipe
			// was since deleted, fall back to the LLM matcher.
			let scriptMatch = null as Awaited<ReturnType<ScriptMatcher['match']>> | null;
			if (directReplay) {
				const boundRecipe = await scriptStore.get(directReplay.recipeId);
				if (boundRecipe) {
					scriptMatch = { tier: 'high', recipe: boundRecipe, extractedParams: directReplay.params } as any;
					logger.info(`[AgentFlow] TTL-expired exact match — replaying bound recipe "${boundRecipe.name}" directly (skipped ScriptMatcher LLM)`);
				} else {
					logger.info(`[AgentFlow] Bound recipe ${directReplay.recipeId} no longer in store — falling back to ScriptMatcher LLM`);
				}
			}
			// Opt-out flag (per-deployment): when SA_SKIP_SCRIPT_MATCH=true, skip the
			// fuzzy ScriptMatcher entirely so only the exact/bound replay above can
			// reuse a script — every other question falls through to fresh generation.
			// Defaults OFF, so other deployments are unaffected.
			if (!scriptMatch && process.env.SA_SKIP_SCRIPT_MATCH !== 'true') {
				const scriptMatcher = new ScriptMatcher(scriptStore);
				scriptMatch = await scriptMatcher.match(prompt, apiKey, sourceAgentModel, signal);
			} else if (!scriptMatch) {
				logger.info('[AgentFlow] ScriptMatcher skipped (SA_SKIP_SCRIPT_MATCH=true) — proceeding with fresh generation');
			}

			// tier='near' diverts BEFORE the runScript replay path. Set up fork
			// context, then fall through to the normal MainAgent flow with a
			// modified prompt.
			if (scriptMatch && scriptMatch.tier === 'near') {
				const parentDepth = scriptMatch.recipe.forkDepth ?? 0;
				if (parentDepth >= FORK_DEPTH_CAP) {
					logger.info(
						`[AgentFlow] Fork-mode skipped: parent "${scriptMatch.recipe.name}" is at fork depth ${parentDepth} ` +
						`(cap=${FORK_DEPTH_CAP}). Falling through to fresh authoring.`,
					);
				} else {
					forkContext = {
						parentRecipeId: scriptMatch.recipe.id,
						parentName: scriptMatch.recipe.name,
						parentDepth,
						parentBody: scriptMatch.recipe.scriptBody,
						gaps: scriptMatch.gaps || [],
						modificationHint: scriptMatch.modificationHint,
					};
					logger.info(
						`[AgentFlow] Fork mode: parent "${scriptMatch.recipe.name}" (depth=${parentDepth}) — ` +
						`MainAgent will adapt the body via write_script/execute_script.`,
					);
				}
			}

			if (scriptMatch && scriptMatch.tier === 'high') {
				logger.info(`[AgentFlow] Script matched: "${scriptMatch.recipe.name}" (tier: high)`);

				try {
					// Run the script in an isolated tsx subprocess with the LLM-extracted parameters.
					// The runner handles IPC, authorizes each ctx.query, and streams per-query UI progress.
					const scriptPath = scriptStore.getScriptPath(scriptMatch.recipe);
					const scriptResult = await runScript(
						scriptMatch.recipe,
						scriptPath,
						scriptMatch.extractedParams || {},
						{ externalTools: agentTools, streamBuffer, signal },
					);

					// runScript resolves (not throws) with success:false on abort. Short-circuit
					// here so we don't misread it as a script failure and fall through to a full
					// MainAgent run (and record a spurious recipe failure).
					if (signal?.aborted) throw new RequestAbortedError();

					if (scriptResult.success && scriptResult.data.length > 0) {
						// Script succeeded — run analysis LLM call
						await scriptStore.recordSuccess(scriptMatch.recipe.id);

						// Build data context for analysis prompt
						const dataContext = scriptResult.executedQueries.map(q => {
							const preview = q.data.slice(0, 15);
							return `## Data from "${q.sourceName}"\nRows: ${q.count}${q.totalCount && q.totalCount > q.count ? ` (${q.totalCount} total)` : ''}\nExecution time: ${q.executionTimeMs}ms\n\n\`\`\`json\n${JSON.stringify(preview, null, 2)}\n\`\`\``;
						}).join('\n\n');

						// Run analysis — 1 LLM call (prompts loaded from file → falls back to prompts.ts)
						const analysisPrompts = await promptLoader.loadPrompts('script-analysis', {
							USER_PROMPT: prompt,
							DATA_CONTEXT: dataContext,
							CURRENT_DATETIME: getCurrentDateTimeForPrompt(),
							GLOBAL_KNOWLEDGE_BASE: globalKnowledgeBase || 'No global knowledge base available.',
							KNOWLEDGE_BASE_CONTEXT: knowledgeBaseContext || 'No additional knowledge base context available.',
							CONVERSATION_HISTORY: conversationHistory || 'No previous conversation',
						});

						const analysisText = await LLM.stream(
							{ sys: analysisPrompts.system, user: analysisPrompts.user },
							{
								model: mainAgentModel || undefined,
								maxTokens: 3000,
								temperature: 0,
								apiKey,
								signal,
								partial: streamBuffer.hasCallback()
									? (chunk: string) => streamBuffer.write(chunk)
									: undefined,
							},
						);

						const scriptTextResponse = (streamBuffer.getFullText() || analysisText || '')
							.replace(/_?_?SB_END_?_?/g, '')
							.replace(/__SB_\w+_(?:START|MSG)_?_?/g, '')
							.replace(/__QUERY_TIMER_START_[^_]*__/g, '')
							.replace(/__QUERY_TIMER_DONE_[\d.]+__/g, '');

						streamBuffer.flush();

						// Build executedTools for component generation (from script query results)
						const scriptExecutedTools: ExecutedToolInfo[] = scriptResult.executedQueries.map(q => ({
							id: q.sourceId,
							name: q.sourceName,
							params: { sql: q.sql },
							result: {
								_totalRecords: q.totalCount || q.count,
								_recordsShown: q.data.length,
								_sampleData: q.data.slice(0, TOOL_TRACKING_SAMPLE_ROWS),
							},
						}));

						// Component generation — lightweight script-components prompt
						// Reuses the script's SQL via queryId, LLM just picks component types + maps columns
						let matchedComponents: Component[] = [];
						let layoutTitle = 'Dashboard';
						let layoutDescription = 'Multi-component dashboard';
						let actions: Action[] = [];

						if (components && components.length > 0 && scriptExecutedTools.length > 0) {
							logger.info(`[AgentFlow] Script flow — generating components (lightweight)`);

							if (streamBuffer.hasCallback()) {
								streamBuffer.write('\n\n📊 **Generating visualization components...**\n\n');
								streamBuffer.write('__TEXT_COMPLETE__COMPONENT_GENERATION_START__');
								streamBuffer.flush();
							}

							// Replay path: do NOT stream __ANSWER_COMPONENT__ markers.
							// Streaming them makes the frontend treat this as an
							// answer-component response (single component up front, the rest
							// hidden behind a "View more details" preload/expand that never
							// completes on replay). Leaving the callback undefined keeps
							// message.isAnswerComponent false on the client, so the final
							// full-dashboard component renders inline with ALL components —
							// identical to the within-TTL exact-match cache hit. Authoring
							// (first ask) still streams the early answer for perceived latency.
							const componentStreamCallback: ((component: Component) => void) | undefined = undefined;

							// Register script SQL in query cache for component reuse.
							// Virtual (computed) datasets get a regeneration descriptor so they
							// can be re-derived on cache expiry instead of showing "expired" (#6).
							const scriptQueryIds: Record<string, string> = {};
							for (const q of scriptResult.executedQueries) {
								scriptQueryIds[q.sourceId] = storeScriptDatasetQuery(q, {
									recipeId: scriptMatch.recipe.id,
									params: scriptMatch.extractedParams || {},
								});
							}

							// Component recipe fast path — rebind stored specs to fresh
							// queryIds deterministically (no component LLM call). Falls back
							// to LLM generation if the recipe has no specs or they drifted.
							let usedRecipeComponents = false;
							if (scriptMatch.recipe.components && scriptMatch.recipe.components.length > 0) {
								const assembled = await assembleComponentsFromSpecs({
									specs: scriptMatch.recipe.components,
									scriptResult,
									queryIds: scriptQueryIds,
									availableComponents: components,
									componentStreamCallback,
									analysisText: scriptTextResponse,
									apiKey,
									model: sourceAgentModel,
								});
								if (assembled && assembled.length > 0) {
									matchedComponents = assembled;
									layoutTitle = scriptMatch.recipe.name || layoutTitle;
									usedRecipeComponents = true;
								}
							}

							if (!usedRecipeComponents) try {
								// Try lightweight script-components prompt first
								const compResult = await generateScriptComponents({
									userPrompt: prompt,
									scriptResult,
									queryIds: scriptQueryIds,
									availableComponents: components,
									apiKey,
									model: sourceAgentModel,
									componentStreamCallback,
									externalTools: agentTools,
									collections,
									analysisText: scriptTextResponse,
									signal,
								});

								if (compResult.components.length > 0) {
									matchedComponents = compResult.components;
									layoutTitle = compResult.layoutTitle;
									layoutDescription = compResult.layoutDescription;
									actions = compResult.actions;
									// Backfill the component recipe so the next replay is deterministic.
									if (compResult.componentSpecs.length > 0) {
										try {
											scriptMatch.recipe.components = compResult.componentSpecs;
											await scriptStore.save(scriptMatch.recipe);
										} catch (e) {
											logger.warn(`[AgentFlow] Failed to backfill component recipe: ${e}`);
										}
									}
								} else {
									// Lightweight generator returned nothing — fall back to full generator
									logger.info(`[AgentFlow] Lightweight component gen returned no components — falling back to full generator`);
									const matchResult = await generateAgentComponents({
										analysisContent: analysisText || scriptTextResponse,
										components,
										userPrompt: prompt,
										executedTools: scriptExecutedTools,
										collections,
										apiKey,
										model: sourceAgentModel,
										componentStreamCallback,
										userId,
										signal,
									});
									matchedComponents = matchResult.components;
									layoutTitle = matchResult.layoutTitle;
									layoutDescription = matchResult.layoutDescription;
									actions = matchResult.actions;
								}
							} catch (compErr) {
								// Don't fall back to the (heavier) full generator if the user aborted.
								if (signal?.aborted || compErr instanceof RequestAbortedError) throw compErr;
								logger.warn(`[AgentFlow] Script component generation failed, trying full generator: ${compErr}`);
								try {
									const matchResult = await generateAgentComponents({
										analysisContent: analysisText || scriptTextResponse,
										components,
										userPrompt: prompt,
										executedTools: scriptExecutedTools,
										collections,
										apiKey,
										model: sourceAgentModel,
										componentStreamCallback,
										userId,
										signal,
									});
									matchedComponents = matchResult.components;
									layoutTitle = matchResult.layoutTitle;
									layoutDescription = matchResult.layoutDescription;
									actions = matchResult.actions;
								} catch (fallbackErr) {
									logger.warn(`[AgentFlow] Full component generation also failed (non-blocking): ${fallbackErr}`);
								}
							}
						}

						// Strip SQL from components — same logic as agent flow
						const queryMap: Record<string, any> = {};
						const securedComponents = matchedComponents.map(comp => {
							const props: any = { ...comp.props };
							const sqlValue = props.externalTool?.parameters?.sql || props.externalTool?.parameters?.query;
							if (sqlValue) {
								const { sql, query, ...restParams } = props.externalTool.parameters;
								const toolId = props.externalTool?.toolId || '';
								const cacheKey = toolId ? `${toolId}:${sqlValue}` : sqlValue;
								const cachedData = queryCache.get(cacheKey);
								const queryId = queryCache.storeQuery(sqlValue, cachedData);
								queryMap[queryId] = sqlValue;
								props.externalTool = { ...props.externalTool, parameters: { queryId, ...restParams } };
							}
							if (props.query) {
								const { query, ...restProps } = props;
								const queryId = queryCache.storeQuery(query);
								queryMap[queryId] = query;
								return { ...comp, props: { ...restProps, queryId } };
							}
							return { ...comp, props };
						});

						let containerComponent: Component | null = null;
						if (securedComponents.length > 0) {
							containerComponent = {
								id: `container_${Date.now()}`,
								name: 'MultiComponentContainer',
								type: 'Container',
								description: layoutDescription,
								props: {
									config: {
										title: layoutTitle,
										description: layoutDescription,
										components: securedComponents,
										...(Object.keys(queryMap).length > 0 && { _queryMap: queryMap }),
									},
									actions,
								},
							};
						}

						// REPLAY path: do NOT generate follow-up actions here. Return them
						// empty so the response renders immediately, and let the frontend
						// fetch them on demand via getActions (ACTIONS_REQ). Regenerating
						// per-request keeps them in sync with the CURRENT data (a re-run on
						// TTL expiry may produce different numbers than when authored), and
						// avoids blocking the replay on a ~3s next-questions LLM call.
						actions = [];
						if (containerComponent) {
							containerComponent.props.actions = [];
						}

						const elapsedTime = Date.now() - startTime;
						logger.info(`[AgentFlow] Script flow complete | ${scriptResult.executedQueries.length} queries | ${matchedComponents.length} components | ${elapsedTime}ms`);

						return {
							success: true,
							data: {
								text: scriptTextResponse,
								component: containerComponent,
								actions,
								method: `${provider}-script-response`,
							},
							errors: [],
						};
					} else {
						// Script failed or returned no data — fall through to agent flow
						logger.warn(`[AgentFlow] Script "${scriptMatch.recipe.name}" failed: ${scriptResult.error || 'no data returned'}`);
						await scriptStore.recordFailure(scriptMatch.recipe.id);
					}
				} catch (scriptErr) {
					// User aborted — propagate (don't fall back to a full agent run or
					// record a spurious recipe failure for a user-initiated stop).
					if (signal?.aborted || scriptErr instanceof RequestAbortedError) throw scriptErr;
					const errMsg = scriptErr instanceof Error ? scriptErr.message : String(scriptErr);
					logger.warn(`[AgentFlow] Script execution error (falling back to agent flow): ${errMsg}`);
					await scriptStore.recordFailure(scriptMatch.recipe.id);
				}
			}
		} else {
			logger.info(`[AgentFlow] No scripts in store — proceeding with agent flow`);
		}

		// If we're in fork mode, prepend a parent-script preamble to the user
		// prompt before MainAgent runs. The agent-main system prompt has the
		// "Fork-and-Adapt Mode" section that explains how to use this block.
		let effectivePrompt = prompt;
		if (forkContext) {
			const gapsBlock = forkContext.gaps.length
				? forkContext.gaps.map(g => `- ${g}`).join('\n')
				: '- (matcher did not list specific gaps; infer from the user question and parent body)';
			const hintBlock = forkContext.modificationHint || '(no specific hint — choose the smallest change that satisfies the user question)';

			effectivePrompt =
				`## Parent Script to Adapt (FORK MODE)\n\n` +
				`The script library has a near-matching parent for this question. ` +
				`Adapt its body via write_script + execute_script — do NOT dispatch source tools unless absolutely necessary. ` +
				`The parent's ctx.query SQL is already proven. Save your modified script under a new descriptive name; the runtime will record the parentage automatically.\n\n` +
				`### Parent name\n${forkContext.parentName}\n\n` +
				`### Parent body\n\`\`\`ts\n${forkContext.parentBody}\n\`\`\`\n\n` +
				`### What needs to change\n${gapsBlock}\n\n` +
				`Hint: ${hintBlock}\n\n` +
				`---\n\n` +
				`User question: ${prompt}\n\n` +
				`---\n\n` +
				`BEGIN NOW. Your VERY FIRST action MUST be a \`write_script\` tool call containing ` +
				`the adapted getData() body, immediately followed by \`execute_script\`. Do NOT reply ` +
				`with prose, a plan, or "I'll adapt…" first — that ends your turn with no script and ` +
				`produces a broken answer. Call \`write_script\` now.`;
		}

		// ============================================
		// STEP 2: MainAgent — route, query, analyze
		// ============================================

		// Source routing pre-filter via embedding search.
		// Only applies when there are 5+ SQL sources to filter — otherwise not worth the risk.
		// Direct tools (toolType='direct') are always kept. Fallback to all tools on error.
		let filteredTools = agentTools;
		const sqlToolCount = agentTools.filter(t => (t as any).toolType !== 'direct').length;
		const routingSearch = collections?.['source-embeddings']?.['search'];
		if (sqlToolCount >= 5 && routingSearch) {
			try {
				const maxSources = Math.min(5, Math.ceil(sqlToolCount * 0.5));
				const res = await routingSearch({
					query: prompt,
					projectId,
					topK: 10,
				});
				const matches = (res?.results || []) as Array<{ sourceId: string; similarity: number; toolType?: string }>;
				// Similarity = 1/(1+L2distance) — 0.35 is loose; top 3 always kept as safety
				const passing = matches.filter(m => m.similarity >= 0.35);
				// Keep at least 3 — user might be asking cross-source
				const keep = passing.length >= 3 ? passing.slice(0, maxSources) : matches.slice(0, 3);
				const keepIds = new Set(keep.map(m => m.sourceId));

				if (keepIds.size > 0) {
					filteredTools = agentTools.filter(t =>
						(t as any).toolType === 'direct' || keepIds.has(t.id),
					);
					logger.info(`[AgentFlow] Source routing pre-filter: ${agentTools.length} → ${filteredTools.length} tools`);
				}
			} catch (err) {
				const msg = err instanceof Error ? err.message : String(err);
				logger.warn(`[AgentFlow] Source routing pre-filter failed (using all tools): ${msg}`);
			}
		}

		// Create and run main agent. In fork mode, MainAgent receives the
		// effectivePrompt (parent body + gaps + hint + user question) instead
		// of the bare user prompt. The system prompt's "Fork-and-Adapt Mode"
		// section tells the LLM how to use the parent block.
		const mainAgent = new MainAgent(filteredTools, agentConfig, scriptStore, turnId, streamBuffer, workflows || [], !!forkContext);
		const agentResponse = await mainAgent.handleQuestion(
			effectivePrompt,
			apiKey,
			conversationHistory,
			streamBuffer.hasCallback() ? (chunk: string) => streamBuffer.write(chunk) : undefined,
			signal,
		);

		// ============================================
		// STEP 2.5: Workflow short-circuit
		// ============================================
		// If the main agent picked a workflow, return that workflow component
		// directly — skip analysis text, skip component generation, skip the
		// federation/SQL rewrite below. The workflow component renders its own
		// UI and fetches its own data via its embedded externalTool config.
		if (agentResponse.workflow) {
			// Flush any streamed pre-amble (e.g., the "🧭 Launching ..." banner)
			streamBuffer.flush();

			const workflowComponent: Component = {
				id: `workflow_${Date.now()}`,
				name: agentResponse.workflow.name,
				type: `Workflow_${agentResponse.workflow.name}`,
				description: `Workflow: ${agentResponse.workflow.name}`,
				props: agentResponse.workflow.props,
			};

			const elapsedTime = Date.now() - startTime;
			logger.info(`[AgentFlow] Workflow short-circuit | "${agentResponse.workflow.name}" | ${elapsedTime}ms`);

			return {
				success: true,
				data: {
					text: '',
					component: workflowComponent,
					actions: [],
					method: `${provider}-agent-workflow`,
				},
				errors: [],
			};
		}

		const rawText = streamBuffer.getFullText() || agentResponse.text || 'I apologize, but I was unable to generate a response.';
		// Strip streaming markers — these are for frontend rendering only,
		// not for component generation or markdown content.
		// Handle both exact (__SB_END__) and potentially truncated (SB_END__) marker formats.
		const textResponse = rawText
			.replace(/_?_?SB_END_?_?/g, '')
			.replace(/__SB_\w+_(?:START|MSG)_?_?/g, '')
			.replace(/__QUERY_TIMER_START_[^_]*__/g, '')
			.replace(/__QUERY_TIMER_DONE_[\d.]+__/g, '')
			.replace(/__TEXT_COMPLETE__COMPONENT_GENERATION_START__/g, '')
			.replace(/\[COMPLEXITY:\s*(?:simple|medium|complex)\]/gi, '');

		// Flush remaining buffered text
		streamBuffer.flush();

		// Abort checkpoint: don't spend a component-generation LLM call if the
		// user already hit Stop while the agent was analyzing.
		if (signal?.aborted) throw new RequestAbortedError();

		// ============================================
		// STEP 3: Component Generation (if data was queried)
		// ============================================

		// Fork-mode adapter — when MainAgent ran in fork mode, it did NOT dispatch
		// any source tool (the parent's SQL ran inside the script subprocess instead).
		// `executedTools` is therefore empty, but `savedScript.executedQueries` carries
		// the rows we want to render. Surface those queries as executedTools +
		// sourceResults so the existing component-generation path produces charts
		// instead of falling into the "general question" branch.
		// See backend/docs/SCRIPT-FLOW-FORK-ADAPT.md.
		if (
			agentResponse.executedTools.length === 0 &&
			agentResponse.savedScript?.executedQueries &&
			agentResponse.savedScript.executedQueries.length > 0
		) {
			for (const q of agentResponse.savedScript.executedQueries) {
				agentResponse.executedTools.push({
					id: q.sourceId,
					name: q.sourceName,
					params: { sql: q.sql },
					result: {
						_totalRecords: q.totalCount || q.count,
						_recordsShown: q.data.length,
						_sampleData: q.data.slice(0, 3),
					},
				});
				agentResponse.sourceResults.push({
					sourceId: q.sourceId,
					sourceName: q.sourceName,
					success: true,
					data: q.data,
					metadata: {
						totalRowsMatched: q.totalCount || q.count,
						rowsReturned: q.count,
						isLimited: !!(q.totalCount && q.totalCount > q.count),
						queryExecuted: q.sql,
						executionTimeMs: q.executionTimeMs,
					},
					executedTool: agentResponse.executedTools[agentResponse.executedTools.length - 1],
				});
			}
			logger.info(
				`[AgentFlow] Fork-mode adapter: surfaced ${agentResponse.savedScript.executedQueries.length} ` +
				`script query result(s) as executedTools for component generation`,
			);
		}

		const hasExecutedTools = agentResponse.executedTools.length > 0;
		let matchedComponents: Component[] = [];
		let layoutTitle = 'Dashboard';
		let layoutDescription = 'Multi-component dashboard';
		let actions: Action[] = [];
		// Validated component specs from the lightweight generator — persisted on
		// the recipe at promote time so future replays are deterministic (Phase 3).
		let authoredComponentSpecs: ScriptComponentSpec[] = [];

		if (!hasExecutedTools) {
			// General question or all queries failed — wrap text in DynamicMarkdownBlock
			// Use agentResponse.text (main agent's final LLM output only), NOT the full
			// streamed text which includes source agent internals (schema searches, SQL, errors)
			logger.info(`[AgentFlow] No tools executed — general question, wrapping in DynamicMarkdownBlock`);

			// Don't spend a follow-up-questions LLM call if the user already aborted.
			if (signal?.aborted) throw new RequestAbortedError();

			const mainAgentText = agentResponse.text || textResponse;

			const nextQuestions = await llmInstance.generateNextQuestions(
				prompt,
				null,
				undefined,
				apiKey,
				conversationHistory,
				mainAgentText,
				signal,
			);
			actions = convertQuestionsToActions(nextQuestions);

			const markdownContent = mainAgentText
				.replace(/<DashboardComponents>[\s\S]*?<\/DashboardComponents>/g, '')
				.trim();

			matchedComponents = [{
				id: 'dynamic-markdown-block',
				name: 'DynamicMarkdownBlock',
				type: 'MarkdownBlock',
				description: 'Text response rendered as markdown',
				props: {
					content: markdownContent
				}
			}];
			layoutTitle = '';
			layoutDescription = '';
		} else if (components && components.length > 0) {
			// Data was queried — generate components
			logger.info(`[AgentFlow] ${agentResponse.executedTools.length} tools executed — generating components`);

			// Send marker for frontend — flush immediately so __TEXT_COMPLETE__ is
			// delivered as its own chunk before any answer component markers arrive
			if (streamBuffer.hasCallback()) {
				streamBuffer.write('\n\n📊 **Generating visualization components...**\n\n');
				streamBuffer.write('__TEXT_COMPLETE__COMPONENT_GENERATION_START__');
				streamBuffer.flush();
			}

			// Component streaming callback — flush immediately so the marker
			// arrives at the frontend before the final USER_PROMPT_RES response
			const componentStreamCallback = streamBuffer.hasCallback() ? (component: Component) => {
				const answerMarker = `__ANSWER_COMPONENT_START__${JSON.stringify(component)}__ANSWER_COMPONENT_END__`;
				streamBuffer.write(answerMarker);
				streamBuffer.flush();
			} : undefined;

			// Try lightweight component generator first (for all cases — single & multi-source)
			// Falls back to full generator only for federation or failure
			const hasSql = agentResponse.executedTools.some(t => t.params?.sql || t.params?.query);
			let usedLightweight = false;

			if (hasSql) {
				logger.info(`[AgentFlow] Trying lightweight component generator`);

				try {
					// Prefer the freshly-authored script's executedQueries when MainAgent
					// wrote + verified a script during this turn. They include the synthetic
					// `computed:_final` entry carrying post-JS computed columns (e.g.,
					// MarketShareSquared, weighted averages), which the raw SourceAgent rows
					// don't have. Without this, the very first run of a script-authored
					// question would chart raw SQL columns and only the second (replay) run
					// would show computed columns. See SCRIPT-FLOW-IMPLEMENTATION.md.
					const agentQueryIds: Record<string, string> = {};
					let agentScriptResult: any;

					if (agentResponse.savedScript?.executedQueries && agentResponse.savedScript.executedQueries.length > 0) {
						logger.info(`[AgentFlow] Using savedScript.executedQueries (${agentResponse.savedScript.executedQueries.length} entries, includes computed columns) for component gen`);
						const scriptQueries = agentResponse.savedScript.executedQueries;
						for (const q of scriptQueries) {
							// Authored scripts run with no params; virtual datasets get a
							// regeneration descriptor for cache-expiry re-derivation (#6).
							agentQueryIds[q.sourceId] = storeScriptDatasetQuery(q, {
								recipeId: agentResponse.savedScript!.recipeId,
								params: {},
							});
						}
						agentScriptResult = {
							success: true,
							data: scriptQueries[0]?.data || [],
							executedQueries: scriptQueries,
							executionTimeMs: 0,
						};
					} else {
						// No script was authored — fall back to SourceAgent's raw rows.
						// IMPORTANT: pull data from `agentResponse.sourceResults` (full rows,
						// up to maxRowsPerSource) — not from `executedTools[].result._sampleData`,
						// which is capped at 3 rows for main-agent prompt efficiency. Using
						// sample data here caused the frontend chart to receive only 3 rows.
						const sourceResultsById = new Map(
							agentResponse.sourceResults.map(sr => [sr.sourceId, sr])
						);
						agentScriptResult = {
							success: true,
							data: agentResponse.sourceResults[0]?.data || [],
							executedQueries: agentResponse.executedTools.map(t => {
								const sql = t.params?.sql || t.params?.query || '';
								const sr = sourceResultsById.get(t.id);
								const fullData = sr?.data || t.result._sampleData || [];
								const qId = queryCache.storeQuery(sql, {
									data: fullData,
									count: sr?.metadata.rowsReturned || t.result._recordsShown || 0,
								});
								agentQueryIds[t.id] = qId;
								return {
									sourceId: t.id,
									sourceName: t.name,
									sql,
									data: fullData,
									count: sr?.metadata.rowsReturned || t.result._recordsShown || 0,
									totalCount: sr?.metadata.totalRowsMatched || t.result._totalRecords,
									executionTimeMs: sr?.metadata.executionTimeMs || 0,
								};
							}),
							executionTimeMs: 0,
						};
					}

					// Retry the script-bound generator on TRANSIENT (network/provider)
					// errors before giving up. Without this, a one-off "Connection error"
					// drops us to the full generator, which writes its OWN SQL and can
					// diverge from the script's answer. Keeping the script-bound path is
					// what guarantees the analysis and the components render the SAME data.
					const compGenArgs = {
						userPrompt: prompt,
						scriptResult: agentScriptResult,
						queryIds: agentQueryIds,
						availableComponents: components,
						apiKey,
						model: sourceAgentModel,
						componentStreamCallback,
						externalTools: agentTools,
						collections,
						analysisText: agentResponse.text,
						signal,
					};
					const MAX_COMP_ATTEMPTS = 3;
					let compResult: Awaited<ReturnType<typeof generateScriptComponents>> | undefined;
					for (let attempt = 1; attempt <= MAX_COMP_ATTEMPTS; attempt++) {
						try {
							compResult = await generateScriptComponents(compGenArgs);
							break;
						} catch (err) {
							const msg = err instanceof Error ? err.message : String(err);
							const transient = /connection error|econnreset|etimedout|fetch failed|socket hang up|overloaded|rate.?limit|\b429\b|\b5\d\d\b|network|timeout/i.test(msg);
							if (!transient || attempt === MAX_COMP_ATTEMPTS) throw err;
							logger.warn(`[AgentFlow] Lightweight component gen transient error (attempt ${attempt}/${MAX_COMP_ATTEMPTS}) — retrying: ${msg}`);
							await new Promise(res => setTimeout(res, 400 * attempt));
						}
					}

					if (compResult && compResult.components.length > 0) {
						matchedComponents = compResult.components;
						layoutTitle = compResult.layoutTitle;
						layoutDescription = compResult.layoutDescription;
						actions = compResult.actions;
						authoredComponentSpecs = compResult.componentSpecs;
						usedLightweight = true;
						logger.info(`[AgentFlow] Lightweight component gen succeeded — ${matchedComponents.length} components`);
					}
				} catch (lightErr) {
					// Don't fall back to the (heavier) full generator if the user aborted.
					if (signal?.aborted || lightErr instanceof RequestAbortedError) throw lightErr;
					logger.warn(`[AgentFlow] Lightweight component gen failed — falling back to full generator: ${lightErr}`);
				}
			}

			if (!usedLightweight) {
				// Full component generator — writes its own SQL, validates, handles federation
				logger.info(`[AgentFlow] Using full component generator`);

				const cleanAnalysis = (agentResponse.text || textResponse)
					.replace(/__SB_\w+_(?:START|MSG)_?_?/g, '')
					.replace(/_?_?SB_END_?_?/g, '')
					.replace(/__QUERY_TIMER_START_[^_]*__/g, '')
					.replace(/__QUERY_TIMER_DONE_[\d.]+__/g, '')
					.replace(/\[COMPLEXITY:\s*(?:simple|medium|complex)\]/gi, '')
					.replace(/<DataTable>[\s\S]*?<\/DataTable>/g,
						'<DataTable>[Data preview removed - for table components, REUSE the exact SQL from EXECUTED_TOOLS parameters. Do NOT write a new query or embed data in props.]</DataTable>'
					);

				// If multiple source tools were executed and a federation tool exists,
				// inject it into executedTools so the component generator can write cross-source JOINs
				const componentExecutedTools = [...agentResponse.executedTools];
				const federationTool = agentTools.find(t => t.id === 'federation_query');
				if (federationTool && agentResponse.executedTools.length >= 2) {
					const sourceToolIds = new Set(agentResponse.executedTools.map(t => t.id));
					const hasMultipleSources = sourceToolIds.size >= 2;
					if (hasMultipleSources) {
						componentExecutedTools.push({
							id: 'federation_query',
							name: 'Cross-Source Query',
							params: { sql: 'DuckDB SQL with schema-qualified table names' },
							result: {
								_totalRecords: 0,
								_recordsShown: 0,
								_sampleData: [],
							},
							outputSchema: federationTool.outputSchema,
							sourceSchema: federationTool.fullSchema || federationTool.description,
							sourceType: 'duckdb',
						});
						logger.info(`[AgentFlow] Injected federation_query tool for cross-source component generation`);
					}
				}

				const matchResult = await generateAgentComponents({
					analysisContent: cleanAnalysis,
					components,
					userPrompt: prompt,
					executedTools: componentExecutedTools,
					collections,
					apiKey,
					model: sourceAgentModel,
					componentStreamCallback,
					userId,
					signal,
				});
				matchedComponents = matchResult.components;
				layoutTitle = matchResult.layoutTitle;
				layoutDescription = matchResult.layoutDescription;
				actions = matchResult.actions;
			}
		}

		// Strip SQL from components — only queryIds sent to frontend
		// Also build a queryMap (queryId → SQL) for persisting alongside the component
		const queryMap: Record<string, any> = {};
		const securedComponents = matchedComponents.map(comp => {
			const props: any = { ...comp.props };

			// Handle externalTool.parameters.sql → replace with queryId
			// Also handle LLM using "query" instead of "sql" as parameter key
			const sqlValue = props.externalTool?.parameters?.sql || props.externalTool?.parameters?.query;
			if (sqlValue) {
				const { sql, query, ...restParams } = props.externalTool.parameters;
				// Pass cached validation data so frontend doesn't re-execute
				const toolId = props.externalTool?.toolId || '';
				const cacheKey = toolId ? `${toolId}:${sqlValue}` : sqlValue;
				const cachedData = queryCache.get(cacheKey);
				const queryId = queryCache.storeQuery(sqlValue, cachedData);
				queryMap[queryId] = sqlValue;
				props.externalTool = {
					...props.externalTool,
					parameters: { queryId, ...restParams },
				};
			}

			// Handle legacy direct props.query → replace with queryId
			if (props.query) {
				const { query, ...restProps } = props;
				const queryId = queryCache.storeQuery(query);
				queryMap[queryId] = query;
				return { ...comp, props: { ...restProps, queryId } };
			}

			return { ...comp, props };
		});

		// Build container component if we have matched components
		let containerComponent: Component | null = null;
		if (securedComponents.length > 0) {
			containerComponent = {
				id: `container_${Date.now()}`,
				name: 'MultiComponentContainer',
				type: 'Container',
				description: layoutDescription,
				props: {
					config: {
						title: layoutTitle,
						description: layoutDescription,
						components: securedComponents,
						// Persist queryId → SQL mapping so cached responses can re-register queries
						...(Object.keys(queryMap).length > 0 && { _queryMap: queryMap })
					},
					actions: actions
				}
			};
		}

		// FRESH ask: the follow-up actions were produced by the component generator
		// in its OWN response (script-components prompt -> compResult.actions, already
		// in `actions`), so components + actions render together from one LLM call —
		// no separate generateNextQuestions round-trip. Just bind them to the payload.
		if (containerComponent) {
			containerComponent.props.actions = actions;
			logger.info(`[AgentFlow] ${actions.length} component-generated follow-up actions ride with the dashboard`);
		}

		// ============================================
		// STEP 2.5: Persist the script MainAgent authored (if any)
		// ============================================
		// MainAgent writes + executes scripts inside its own turn via write_script /
		// execute_script. When a draft passed verification, it arrives here on
		// agentResponse.savedScript — we just persist it. No post-hoc LLM generation.

		// MainAgent already persisted the draft to disk via ScriptStore.saveDraft()
		// at write_script time. We just need to promote it: flip status='verified',
		// apply provenance + optional fork lineage, and (when possible) drop the
		// turn suffix from the on-disk filename. Failed drafts (no savedScript)
		// stay on disk with status='draft' for the user to inspect.
		if (agentResponse.savedScript) {
			try {
				const authored = agentResponse.savedScript;

				// Lineage from forkContext (when matcher returned tier='near').
				const lineage = forkContext
					? {
						parentId: forkContext.parentRecipeId,
						forkDepth: Math.min(FORK_DEPTH_CAP, forkContext.parentDepth + 1),
						forkReason: forkContext.modificationHint,
					}
					: undefined;

				const promoted = await scriptStore.promoteToVerified(authored.recipeId, {
					sourceIds: authored.sourceIds,
					tables: authored.tables,
					...lineage,
					components: authoredComponentSpecs,
				});

				if (promoted && lineage?.parentId) {
					logger.info(
						`[AgentFlow] Fork persisted: "${authored.name}" → parent "${forkContext!.parentName}" ` +
						`(depth ${promoted.forkDepth})`,
					);
				}
			} catch (err) {
				const msg = err instanceof Error ? err.message : String(err);
				logger.warn(`[AgentFlow] Failed to promote authored script (non-blocking): ${msg}`);
			}
		}

		const elapsedTime = Date.now() - startTime;
		logger.info(`[AgentFlow] Complete | ${agentResponse.executedTools.length} tools | ${matchedComponents.length} components | ${elapsedTime}ms`);

		return {
			success: true,
			data: {
				text: textResponse,
				component: containerComponent,
				actions: actions,
				method: `${provider}-agent-response`
			},
			errors: []
		};

	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);

		// User cancelled (Stop button) — return a clean, non-error result. The
		// frontend already marked the message "stopped" and dropped late chunks,
		// so we just unwind quietly without logging a failure or rendering an
		// error component. Partial streamed text stays on screen by design.
		if (error instanceof RequestAbortedError || signal?.aborted) {
			const elapsedTime = Date.now() - startTime;
			logger.info(`[AgentFlow] Aborted by user after ${elapsedTime}ms`);
			return {
				success: false,
				errors: ['aborted'],
				data: {
					text: '',
					method: `${provider}-agent-aborted`,
				},
			};
		}

		logger.error(`[AgentFlow] Error: ${errorMsg}`);

		userPromptErrorLogger.logError(
			'agentUserResponse',
			error instanceof Error ? error : new Error(errorMsg),
			{ userPrompt: prompt }
		);

		const elapsedTime = Date.now() - startTime;
		logger.info(`[AgentFlow] Failed in ${elapsedTime}ms`);

		return {
			success: false,
			errors: [errorMsg],
			data: {
				text: 'I apologize, but I encountered an error processing your request. Please try again.',
				method: `${provider}-agent-error`
			}
		};
	}
};
